import { ArrowRight, ArrowUpRight, Filter, MapPin, Phone } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "wouter";
import { QuoteBand } from "@/components/QuoteBand";
import { StructuredData } from "@/components/StructuredData";
import {
  projectGalleryFacilityTypes,
  projectGalleryItems,
  projectGalleryMaterials,
  projectGalleryStates,
} from "@/data/projectGallery";
import { realPhotos, siteConfig } from "@/data/site";

type GalleryFilter = {
  state: string;
  material: string;
  facility: string;
};

const allStates = "All states";
const allMaterials = "All documented materials";
const allFacilities = "All facility types";
const HERO_VIDEO_START_DELAY_MS = 180;
const projectsHeroBanner = {
  video: "/manus-storage/PartitionStalls_Header_Banner_21x9_1920x824-original_34a61cab.mp4",
};

export default function Projects() {
  const [filters, setFilters] = useState<GalleryFilter>({
    state: allStates,
    material: allMaterials,
    facility: allFacilities,
  });
  const hasActiveFilters = filters.state !== allStates || filters.material !== allMaterials || filters.facility !== allFacilities;
  const heroVideoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = heroVideoRef.current;
    if (!video) return;
    let startAllowed = false;

    const playWhenVisible = () => {
      if (!startAllowed || document.visibilityState !== "visible" || video.ended) return;
      void video.play().catch(() => undefined);
    };

    // Preserve the untouched original video while letting the page and its controls
    // paint first. Playback bytes are requested only after that first paint and do
    // not continue while the banner cannot be seen.
    const startTimer = window.setTimeout(() => {
      startAllowed = true;
      video.currentTime = 0;
      playWhenVisible();
    }, HERO_VIDEO_START_DELAY_MS);
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) playWhenVisible();
        else video.pause();
      },
      { threshold: 0.15 },
    );
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") playWhenVisible();
      else video.pause();
    };

    observer.observe(video);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.clearTimeout(startTimer);
      observer.disconnect();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      video.pause();
    };
  }, []);

  const visibleProjects = useMemo(
    () => projectGalleryItems.filter((item) => (
      (filters.state === allStates || item.stateName === filters.state)
      && (filters.material === allMaterials || item.materialFilters.includes(filters.material as never))
      && (filters.facility === allFacilities || item.facilityType === filters.facility)
    )),
    [filters],
  );

  const url = `${siteConfig.url}/projects`;
  const collectionSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "@id": `${url}#breadcrumb`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: siteConfig.url },
          { "@type": "ListItem", position: 2, name: "Projects", item: url },
        ],
      },
      {
        "@type": "CollectionPage",
        "@id": `${url}#webpage`,
        url,
        name: "Commercial Restroom Project Gallery | Partition Stalls",
        description: "Browse owner-documented commercial bathroom partition projects by state, documented material, and facility type.",
        isPartOf: { "@id": `${siteConfig.url}/#website` },
        about: { "@id": `${siteConfig.url}/#business` },
        breadcrumb: { "@id": `${url}#breadcrumb` },
        mainEntity: { "@id": `${url}#projects` },
      },
      {
        "@type": "ItemList",
        "@id": `${url}#projects`,
        name: "Owner-documented Partition Stalls commercial restroom projects",
        numberOfItems: projectGalleryItems.length,
        itemListElement: projectGalleryItems.map((item, index) => {
          const photo = realPhotos.projects[item.project.imageKey];
          return {
            "@type": "ListItem",
            position: index + 1,
            item: {
              "@type": "CreativeWork",
              "@id": `${url}#project-${index + 1}`,
              name: item.project.title,
              description: item.project.summary,
              ...(item.project.imageKind === "material-reference" ? {} : { image: `${siteConfig.url}${photo.large}` }),
              url: `${siteConfig.url}${item.projectPath}`,
              contentLocation: { "@type": "State", name: item.stateName },
              publisher: { "@id": `${siteConfig.url}/#business` },
            },
          };
        }),
      },
    ],
  };

  return (
    <>
      <StructuredData data={collectionSchema} />

      <header className="projects-hero section-line">
        <figure className="projects-hero__banner">
          <video ref={heroVideoRef} aria-label="Partition Stalls commercial restroom partition material banner" muted playsInline preload="none">
            <source src={projectsHeroBanner.video} type="video/mp4" />
          </video>
        </figure>
      </header>

      <section id="project-browser" className="projects-browser projects-browser--intro section-line" aria-labelledby="projects-browser-title">
        <div className="projects-browser__heading">
          <div>
            <h1 id="projects-browser-title">Jobs near you.</h1>
          </div>
        </div>
      </section>

      <section className="projects-browser projects-browser--gallery section-line" aria-label="Project gallery filters and records">
        <div className="projects-filters" aria-label="Filter project gallery">
          <div className="projects-filters__label"><Filter aria-hidden="true" /><span>Filter projects</span><small>Use one filter or combine them.</small></div>
          <label>
            <span>State</span>
            <select value={filters.state} onChange={(event) => setFilters((current) => ({ ...current, state: event.target.value }))}>
              <option value={allStates}>{allStates}</option>
              {projectGalleryStates.map((state) => <option key={state} value={state}>{state}</option>)}
            </select>
          </label>
          <label>
            <span>Documented material</span>
            <select value={filters.material} onChange={(event) => setFilters((current) => ({ ...current, material: event.target.value }))}>
              <option value={allMaterials}>{allMaterials}</option>
              {projectGalleryMaterials.map((material) => <option key={material} value={material}>{material}</option>)}
            </select>
          </label>
          <label>
            <span>Facility type</span>
            <select value={filters.facility} onChange={(event) => setFilters((current) => ({ ...current, facility: event.target.value }))}>
              <option value={allFacilities}>{allFacilities}</option>
              {projectGalleryFacilityTypes.map((facility) => <option key={facility} value={facility}>{facility}</option>)}
            </select>
          </label>
          <button type="button" className="projects-filters__reset" onClick={() => setFilters({ state: allStates, material: allMaterials, facility: allFacilities })} disabled={!hasActiveFilters}>Clear filters</button>
        </div>

        <p className="projects-results" aria-live="polite"><strong>{visibleProjects.length}</strong> {visibleProjects.length === 1 ? "project" : "projects"} shown{hasActiveFilters ? " for the filters you chose." : "."}</p>

        <div className="projects-grid">
          {visibleProjects.map((item) => {
            const photo = realPhotos.projects[item.project.imageKey];
            return (
              <article className="project-card" key={item.project.imageKey} id={item.recordKind === "gallery-only" ? `project-${item.project.imageKey.replace(/([A-Z])/g, "-$1").toLowerCase().replace(/-primary$/, "")}` : undefined}>
                <figure>
                  <picture>
                    <source media="(max-width: 640px)" srcSet={photo.small} />
                    <source media="(max-width: 1100px)" srcSet={photo.medium} />
                    <img
                      src={photo.large}
                      srcSet={`${photo.small} 640w, ${photo.medium} 1000w, ${photo.large} 1600w`}
                      sizes="(max-width: 640px) 100vw, (max-width: 1100px) 50vw, 33vw"
                      alt={item.project.imageAlt}
                      width={item.project.imageWidth}
                      height={item.project.imageHeight}
                      loading="lazy"
                      decoding="async"
                    />
                  </picture>
                  <figcaption>{item.project.imageKind === "material-reference" ? "Plastic Laminate material reference · not a project photograph" : "Owner-provided project photograph · Partition Stalls"}</figcaption>
                </figure>
                {item.recordKind === "gallery-only" ? item.project.gallery?.images.map((galleryImage) => {
                  const galleryPhoto = realPhotos.projects[galleryImage.imageKey];
                  return (
                    <figure key={galleryImage.imageKey}>
                      <picture>
                        <source media="(max-width: 640px)" srcSet={galleryPhoto.small} />
                        <source media="(max-width: 1100px)" srcSet={galleryPhoto.medium} />
                        <img
                          src={galleryPhoto.large}
                          srcSet={`${galleryPhoto.small} 640w, ${galleryPhoto.medium} 1000w, ${galleryPhoto.large} 1600w`}
                          sizes="(max-width: 640px) 100vw, (max-width: 1100px) 50vw, 33vw"
                          alt={galleryImage.alt}
                          width={galleryImage.width}
                          height={galleryImage.height}
                          loading="lazy"
                          decoding="async"
                        />
                      </picture>
                      <figcaption>{galleryImage.caption}</figcaption>
                    </figure>
                  );
                }) : null}
                <div className="project-card__copy">
                  <div className="project-card__eyebrow"><span>{item.stateName}</span><span>{item.facilityType}</span></div>
                  <h3>{item.project.title}</h3>
                  <p className="project-card__location"><MapPin aria-hidden="true" /> {item.project.location}</p>
                  <p className="project-card__summary">{item.project.summary}</p>
                  <dl className="project-card__facts">
                    {item.materialFilters.length ? (
                      <div><dt>Documented material</dt><dd>{item.materialFilters.join(" + ")}</dd></div>
                    ) : null}
                    <div><dt>Full record</dt><dd>{item.recordKind === "state-guide" ? `${item.stateName} project guide` : "Owner-documented gallery record"}</dd></div>
                  </dl>
                  {item.recordKind === "gallery-only" ? <p className="project-card__disclosure">{item.project.disclosure}</p> : null}
                  <div className="project-card__actions">
                    {item.recordKind === "state-guide" ? <Link href={item.projectPath}>View project record <ArrowRight aria-hidden="true" /></Link> : null}
                    {item.project.venueUrl ? <a href={item.project.venueUrl} target="_blank" rel="noreferrer">Venue source <ArrowUpRight aria-hidden="true" /></a> : null}
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {visibleProjects.length === 0 ? (
          <div className="projects-empty">
            <h3>No exact match yet.</h3>
            <p>Try a broader filter, or send the project plans, measurements, photos, quantities, and location for a project-specific review.</p>
            <Link href="/request-a-quote" className="button button--primary">Request a Quote <ArrowUpRight aria-hidden="true" /></Link>
          </div>
        ) : null}
      </section>

      <section className="projects-next-step section-line" aria-labelledby="projects-next-step-title">
        <div><span className="kicker kicker--light">Ready to plan your project?</span><h2 id="projects-next-step-title">Send the details that affect the quote.</h2></div>
        <p>Plans or field measurements, material direction, quantities, accessories, project location, schedule, and site conditions help the team review the complete commercial restroom scope.</p>
        <Link href="/request-a-quote" className="button button--light">Start a Quote <ArrowUpRight aria-hidden="true" /></Link>
      </section>

      <QuoteBand quoteHref="/request-a-quote" title="Bring your next restroom project into focus." copy="Send plans, measurements, photos, a product list, or the project location. Partition Stalls will review the commercial partition and accessory scope with you." />
    </>
  );
}
